import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime, of, switchMap, catchError, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlightService } from '../../../services/flight/flight.service';
import { Airport } from '../../../models/airport';

/** typed results: up to 8 airports */
const MAX_RESULTS = 8;
const MIN_TERM = 3;
const MAX_RECENT = 4;

type Group = { city: string; country: string; airports: Airport[] };

/**
 * From / To of the results-page search (WI 3562, baseline 4): the traveller always picks an airport.
 * - a popular city opens "‹ All cities · Airports in London · 4" with that city's airports (also when only one);
 * - typed results are grouped by city ("Cairo, Egypt · 2 airports"), city matches first, up to 8 airports,
 *   each in the live-site format "Egypt, Cairo, SPX, Sphinx International Airport";
 * - a recent search is an airport: one tap picks it.
 * `overlay` (desktop): the panel opens exactly over the field, with the field (input) on top.
 * `below` (phone sheet): the field itself is the input, the panel opens 6 px under it (or under its row).
 */
@Component({
  selector: 'app-airport-picker',
  imports: [NgTemplateOutlet, TranslateModule],
  templateUrl: './airport-picker.component.html',
  // the prototype's stylesheet (results-search.proto.css, loaded by the search) styles this markup
  encapsulation: ViewEncapsulation.None,
  host: { style: 'display: contents' },
})
export class AirportPickerComponent implements AfterViewInit {
  private flightService = inject(FlightService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('field') field?: ElementRef<HTMLInputElement>;

  cityType = input<'from' | 'to'>('from');
  index = input(0);
  variant = input<'overlay' | 'below'>('overlay');
  /** the airport chosen now (its city is marked in Popular cities) */
  current = input<Airport | null>(null);

  picked = output<{ type: 'from' | 'to'; airport: Airport; index: number }>();
  cancel = output<void>();

  /** the prototype's Popular cities (QPOP), in its order */
  readonly popularCities = [
    'Jeddah',
    'Riyadh',
    'Madinah',
    'Hail',
    'AlUla',
    'Cairo',
    'Rome',
    'Milan',
    'Venice',
    'Barcelona',
    'Amsterdam',
    'London',
    'Santorini',
    'Tokyo',
    'Dubai',
    'Bali',
    'Sydney',
  ];

  term = signal('');
  /** the popular city opened ("Airports in London") */
  city = signal<string | null>(null);
  loading = signal(false);
  airports = signal<Airport[]>([]);
  recent = signal<Airport[]>(readRecent());
  /** keyboard: the airport highlighted in the list */
  active = signal(-1);

  private search$ = new Subject<string>();

  /** typed results grouped by city, city matches first, 8 airports at most */
  groups = computed<Group[]>(() => {
    const term = this.term().trim().toLowerCase();
    const map = new Map<string, Group>();
    for (const a of this.airports()) {
      const key = `${a.city}|${a.country}`;
      if (!map.has(key)) map.set(key, { city: a.city, country: a.country, airports: [] });
      map.get(key)!.airports.push(a);
    }
    const all = [...map.values()];
    const cityFirst = [
      ...all.filter((g) => g.city?.toLowerCase().startsWith(term)),
      ...all.filter((g) => !g.city?.toLowerCase().startsWith(term)),
    ];

    let left = MAX_RESULTS;
    const out: Group[] = [];
    for (const g of cityFirst) {
      if (left <= 0) break;
      const airports = g.airports.slice(0, left);
      left -= airports.length;
      out.push({ ...g, airports });
    }
    return out;
  });

  /** the airports of the opened popular city (Rome, Italy: not Rome, United States) */
  cityAirports = computed(() => {
    const city = this.city()?.toLowerCase();
    const same = this.airports().filter((a) => a.city?.toLowerCase() === city);
    const count = new Map<string, number>();
    same.forEach((a) => count.set(a.country, (count.get(a.country) ?? 0) + 1));
    const country = [...count.entries()].sort((x, y) => y[1] - x[1])[0]?.[0];
    return same.filter((a) => a.country === country);
  });

  /** the airports in the order shown, for the keyboard */
  options = computed<Airport[]>(() =>
    this.city() ? this.cityAirports() : this.groups().flatMap((g) => g.airports)
  );

  view = computed<'home' | 'city' | 'typed'>(() =>
    this.city() ? 'city' : this.term().trim().length >= MIN_TERM ? 'typed' : 'home'
  );

  constructor() {
    this.search$
      .pipe(
        debounceTime(200),
        tap(() => this.loading.set(true)),
        switchMap((q) => this.flightService.searchAirports(q).pipe(catchError(() => of([])))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.airports.set(res ?? []);
        this.loading.set(false);
        this.active.set(-1);
      });
  }

  ngAfterViewInit(): void {
    // open fields keep their place: no scroll on focus
    this.field?.nativeElement.focus({ preventScroll: true });
  }

  onType(value: string) {
    this.term.set(value);
    this.city.set(null);
    if (value.trim().length >= MIN_TERM) {
      this.search$.next(value.trim());
    } else {
      this.airports.set([]);
      this.loading.set(false);
    }
  }

  clearTerm() {
    this.onType('');
    this.field?.nativeElement.focus({ preventScroll: true });
  }

  openCity(city: string) {
    this.term.set('');
    if (this.field) this.field.nativeElement.value = '';
    this.airports.set([]);
    this.city.set(city);
    this.search$.next(city);
  }

  backToCities() {
    this.city.set(null);
    this.airports.set([]);
    this.field?.nativeElement.focus({ preventScroll: true });
  }

  /** the picked airport becomes the first recent search (4 at most) */
  pick(airport: Airport) {
    const recent = [airport, ...this.recent().filter((a) => a.iata !== airport.iata)].slice(0, MAX_RECENT);
    this.recent.set(recent);
    try {
      sessionStorage.setItem('recentCities', JSON.stringify(recent));
    } catch {}
    this.picked.emit({ type: this.cityType(), airport, index: this.index() });
  }

  clearRecent() {
    sessionStorage.removeItem('recentCities');
    this.recent.set([]);
  }

  isChosenCity(city: string): boolean {
    return this.current()?.city?.toLowerCase() === city.toLowerCase();
  }

  /** ↑ ↓ move in the list, Enter picks */
  onKey(event: KeyboardEvent) {
    const list = this.options();
    if (!list.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.active.set((this.active() + 1) % list.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.active.set((this.active() - 1 + list.length) % list.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const airport = list[Math.max(0, this.active())];
      if (airport) this.pick(airport);
    }
  }

  indexOf(airport: Airport): number {
    return this.options().indexOf(airport);
  }

  get labelKey(): string {
    return this.cityType() === 'from' ? 'Search.From' : 'Search.To';
  }
}

function readRecent(): Airport[] {
  try {
    const stored = sessionStorage.getItem('recentCities');
    return stored ? (JSON.parse(stored) as Airport[]).slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}
